/**
 * Sample platform KPI metrics until /api/platform/kpi/* exists (PLATFORM-KPI-DESIGN.md §8.1).
 * Generated for the REAL center list so names, ids and drill-downs line up. Values are deterministic
 * per center id, so they don't jump on refresh. Each center gets one archetype, so the sample spreads
 * across every ladder stage, quadrant and health level.
 */

const DAY = 86400000;
const PERIOD_FACTOR = { today: 1 / 30, '7d': 7 / 30, '30d': 1, '90d': 3 };
// Sample monthly prices (AED). Real prices come from plan_prices once the revenue API exists.
export const SAMPLE_PRICES = { BASIC: 199, PREMIUM: 499, ENTERPRISE: 999 };

const hash = (s) => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return Math.abs(h);
};
const at = (base, days) => new Date(new Date(base).getTime() + days * DAY).toISOString();
const ago = (now, days) => (days == null ? null : new Date(now.getTime() - days * DAY).toISOString());
const features = (n) => Object.fromEntries(
  ['workOrders', 'invoices', 'receipts', 'aiExtraction', 'quotations', 'wallets', 'accounting', 'portal'].map((k, i) => [k, i < n]),
);
const dayStart = (d) => new Date(Math.floor(d.getTime() / DAY) * DAY);

// [potential inputs, ladder inputs, activity, plan] per archetype — see design §3.3–3.8.
const ARCHETYPES = [
  { key: 'champion', clients: 42, employees: 1350, staffCount: 6, hasService: true, started: true, weeksActiveLast4: 4, featureCount: 7,
    wo30d: 120, woCompleted30d: 110, woInvoiced30d: 100, invoices30d: 95, receipts30d: 80, uploads30d: 300, woLast4w: 120, woPrev4w: 110,
    idleDays: 0, gmv30d: 185000, aiTokens30d: 2100000, tier: 'PREMIUM', autoRenew: true, renewInDays: 40 },
  { key: 'big_idle', clients: 28, employees: 640, staffCount: 3, hasService: true, started: false, weeksActiveLast4: 1, featureCount: 1,
    wo30d: 0, woCompleted30d: 0, woInvoiced30d: 0, invoices30d: 0, receipts30d: 0, uploads30d: 45, woLast4w: 0, woPrev4w: 0,
    idleDays: 9, gmv30d: 0, aiTokens30d: 380000, tier: 'FREE' },
  { key: 'habit_small', clients: 3, employees: 40, staffCount: 2, hasService: true, started: true, weeksActiveLast4: 4, featureCount: 3,
    wo30d: 25, woCompleted30d: 22, woInvoiced30d: 20, invoices30d: 18, receipts30d: 15, uploads30d: 30, woLast4w: 25, woPrev4w: 22,
    idleDays: 1, gmv30d: 21000, aiTokens30d: 160000, tier: 'BASIC', autoRenew: true, renewInDays: 12 },
  { key: 'started_leaky', clients: 8, employees: 180, staffCount: 3, hasService: true, started: true, weeksActiveLast4: 2, featureCount: 2,
    wo30d: 16, woCompleted30d: 14, woInvoiced30d: 2, invoices30d: 2, receipts30d: 0, uploads30d: 40, woLast4w: 16, woPrev4w: 14,
    idleDays: 3, gmv30d: 4800, aiTokens30d: 220000, tier: 'BASIC', autoRenew: false, renewInDays: 5 },
  { key: 'new', clients: 0, employees: 0, staffCount: 1, hasService: false, started: false, weeksActiveLast4: 0, featureCount: 0,
    wo30d: 0, woCompleted30d: 0, woInvoiced30d: 0, invoices30d: 0, receipts30d: 0, uploads30d: 0, woLast4w: 0, woPrev4w: 0,
    idleDays: null, gmv30d: 0, aiTokens30d: 0, tier: 'FREE' },
  { key: 'fading', clients: 12, employees: 260, staffCount: 4, hasService: true, started: true, weeksActiveLast4: 2, featureCount: 4,
    wo30d: 12, woCompleted30d: 12, woInvoiced30d: 11, invoices30d: 11, receipts30d: 9, uploads30d: 20, woLast4w: 12, woPrev4w: 60,
    idleDays: 18, gmv30d: 9600, aiTokens30d: 90000, tier: 'BASIC', autoRenew: true, renewInDays: 3, failedRenewal: true },
  { key: 'admin_only', clients: 6, employees: 70, staffCount: 3, hasService: true, started: true, weeksActiveLast4: 3, featureCount: 3,
    wo30d: 30, woCompleted30d: 28, woInvoiced30d: 26, invoices30d: 26, receipts30d: 20, uploads30d: 35, woLast4w: 30, woPrev4w: 28,
    idleDays: 2, gmv30d: 26500, aiTokens30d: 140000, tier: 'BASIC', autoRenew: true, renewInDays: 70, adminOnly30d: true },
];

const pickArchetype = (center, index) => ARCHETYPES[(index ?? hash(center.id || '')) % ARCHETYPES.length];

const periodFactor = (period, now) => {
  if (period === 'mtd') return new Date(now.getTime() + 4 * 3600000).getUTCDate() / 30;
  return PERIOD_FACTOR[period] ?? 1;
};

/** Sample per-center metrics. `index` (position in the center list) spreads archetypes evenly. */
export const sampleCenterMetrics = (center, period, index, now = new Date()) => {
  const a = pickArchetype(center, index);
  const today = dayStart(now);
  const createdAt = center.createdAt || ago(today, 60 + (hash(center.id || '') % 200));
  const f = periodFactor(period, now);
  const jitter = 0.85 + (hash(`${center.id}:${period}`) % 30) / 100; // 0.85–1.14, stable per center+period
  const scale = (v) => Math.round(v * f * jitter);
  return {
    archetype: a.key,
    clients: a.clients,
    employees: a.employees,
    employeesOnFile: a.employees,
    clientsAdded: Math.round(a.clients * 0.08 * f * jitter),
    employeesAdded: Math.round(a.employees * 0.06 * f * jitter),
    staffCount: a.staffCount,
    hasService: a.hasService,
    createdAt,
    firstWorkOrderAt: a.started ? at(createdAt, 4) : null,
    firstInvoiceAt: a.started ? at(createdAt, 6) : null,
    weeksActiveLast4: a.weeksActiveLast4,
    features: features(a.featureCount),
    wo30d: a.wo30d,
    woCompleted30d: a.woCompleted30d,
    woInvoiced30d: a.woInvoiced30d,
    invoices30d: a.invoices30d,
    receipts30d: a.receipts30d,
    uploads30d: a.uploads30d,
    woLast4w: a.woLast4w,
    woPrev4w: a.woPrev4w,
    adminOnly30d: !!a.adminOnly30d,
    failedRenewal: !!a.failedRenewal,
    lastBusinessAt: ago(today, a.idleDays),
    gmvInvoiced: scale(a.gmv30d),
    gmvCollected: scale(a.gmv30d * 0.82),
    aiTokens: scale(a.aiTokens30d),
    storageBytes: a.employees * 2.4 * 1024 * 1024,
    samplePlan: {
      name: { FREE: 'Free', BASIC: 'Basic', PREMIUM: 'Premium' }[a.tier],
      tier: a.tier,
      status: a.tier === 'FREE' ? 'FREE' : 'ACTIVE',
      paid: a.tier !== 'FREE',
      autoRenew: !!a.autoRenew,
      periodEnd: a.renewInDays != null ? ago(today, -a.renewInDays).slice(0, 10) : null,
    },
  };
};

/** Sample Thaklis Pro revenue built from the (live or sample) plans of the given centers. */
export const sampleRevenue = (centers, period, now = new Date()) => {
  const paidCenters = centers.filter((c) => c.plan?.paid);
  const mrr = paidCenters.reduce((s, c) => s + (SAMPLE_PRICES[c.plan.tier] || 0), 0);
  const paying = paidCenters.length;
  const free = centers.length - paying;
  const f = Math.max(periodFactor(period, now), 1 / 30);
  const newMrr = paying ? Math.round(SAMPLE_PRICES.BASIC * Math.min(1, f)) : 0;
  const churnMrr = paying > 2 ? Math.round(SAMPLE_PRICES.BASIC * Math.min(1, f)) : 0;
  const movements = {
    new: newMrr,
    expansion: paying > 1 ? SAMPLE_PRICES.PREMIUM - SAMPLE_PRICES.BASIC : 0,
    reactivation: 0,
    contraction: 0,
    churn: churnMrr,
  };
  const mrrPrev = mrr - (movements.new + movements.expansion + movements.reactivation - movements.contraction - movements.churn);
  const local = new Date(now.getTime() + 4 * 3600000);
  const months = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(Date.UTC(local.getUTCFullYear(), local.getUTCMonth() - (11 - i), 1));
    return { month: d.toISOString().slice(0, 7), mrr: Math.max(0, Math.round(mrr * (0.35 + (0.65 * i) / 11))) };
  });
  const churned = churnMrr ? 1 : 0;
  const converted = paying && free ? 1 : 0;
  return {
    mrr,
    mrrPrev,
    movements,
    paying,
    free,
    freeGrant: centers.length > 3 ? 1 : 0,
    arpa: paying ? Math.round(mrr / paying) : null,
    churned,
    payingAtStart: paying - (newMrr ? 1 : 0) + churned,
    converted,
    freeAtStart: free + converted,
    failedRenewals: centers.filter((c) => c.failedRenewal).length,
    recovered: 0,
    discounts: paying ? 150 : 0,
    months,
  };
};

/**
 * Sample month-by-month history for the Growth tab. Each series ends at today's totals
 * so Growth and Overview agree; earlier months ramp up with a small deterministic wobble.
 * Stocks ramp from ~40%; flows wobble around a rising trend.
 */
export const sampleGrowth = (centers, months, revenue) => {
  const n = months.length;
  const sum = (key) => centers.reduce((s, c) => s + (c[key] || 0), 0);
  const ramp = (total, kind) => months.map((m, i) => {
    if (i === n - 1) return total;
    const t = n > 1 ? i / (n - 1) : 1;
    const wobble = 0.94 + (hash(`${m}`) % 13) / 100;
    const base = kind === 'flow' ? 0.55 + 0.45 * t : 0.4 + 0.6 * Math.pow(t, 1.3);
    return Math.max(0, Math.round(total * base * wobble));
  });
  // A month-to-date flow is partial; the sample keeps it partial too (about two-thirds).
  const flow = (total) => {
    const full = Math.round(total * 1.5);
    const series = ramp(full, 'flow');
    series[n - 1] = total;
    return series;
  };
  const mrrByMonth = Object.fromEntries((revenue?.months || []).map((r) => [r.month, r.mrr]));
  return {
    payingCenters: ramp(centers.filter((c) => c.plan?.paid).length, 'stock'),
    mrr: months.map((m, i) => mrrByMonth[m] ?? (i === n - 1 ? revenue?.mrr ?? 0 : 0)),
    clients: ramp(sum('clients'), 'stock'),
    employees: ramp(sum('employees'), 'stock'),
    activeUsers: ramp(Math.round(sum('staffCount') * 0.8), 'stock'),
    workOrders: flow(sum('wo30d')),
    invoiced: flow(sum('gmvInvoiced')),
    storage: ramp(sum('storageBytes'), 'stock'),
  };
};
